import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface StatCardProps {
  label: string;
  value: string;
  icon: LucideIcon;
  iconBg?: string;
  iconColor?: string;
  trend?: number;
  trendLabel?: string;
  onClick?: () => void;
  className?: string;
}

export default function StatCard({
  label,
  value,
  icon: Icon,
  iconBg = "bg-primary-50",
  iconColor = "text-primary-600",
  trend,
  trendLabel,
  onClick,
  className = "",
}: StatCardProps) {
  const positive = (trend ?? 0) >= 0;
  const isClickable = !!onClick;

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`group relative overflow-hidden rounded-xl border border-slate-200/80 bg-white p-4 sm:p-5 shadow-sm transition-all duration-200 ${
        isClickable
          ? "cursor-pointer hover:-translate-y-1 hover:border-primary-400 hover:shadow-md active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          : "hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
      } ${className}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] sm:text-xs font-semibold tracking-wider uppercase text-slate-500 truncate" title={label}>
          {label}
        </span>
        <div
          className={`flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg transition-transform duration-200 group-hover:scale-105 ${iconBg} ${iconColor}`}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 sm:mt-2.5">
        <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 whitespace-nowrap">
          {value}
        </span>
      </div>
      {(trend !== undefined || trendLabel) && (
        <div className="mt-3 flex items-center gap-1.5 pt-2 border-t border-slate-100 min-w-0">
          {trend !== undefined && (
            <span
              className={`inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold ${
                positive ? "text-emerald-600" : "text-rose-600"
              }`}
            >
              {positive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
              {positive ? "+" : ""}
              {trend}%
            </span>
          )}
          {trendLabel && <span className="text-xs font-medium text-slate-500 truncate" title={trendLabel}>{trendLabel}</span>}
        </div>
      )}
    </div>
  );
}